import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { getContactType } from '@core/util/encumbrance';
import { createId } from '@core/util/file';
import { AddressFields } from '@models/address';
import { CompanyFields } from '@models/company';
import {
	Branch,
	ContactType,
	institutionFields,
	MortgageeType,
} from '@models/lender';
import { MatterFileType } from '@models/matter';
import { GlobalDataManagementRequestDispatch } from 'src/app/global-data-management/state/global-data-management/global-data-management.request.dispatch';
import { InstitutionBadgeDetails } from 'src/app/shared/atomic/organisms/institution-badge/institution-badge.component';
import { ContactTypes } from '../create-institution/create-institution.component';

export interface CreateOrUpdateInstitutionModel {
	showDischargeFields: boolean;
	branch?: Branch;
	institutionId?: string;
	branchLabel?: string;
	showTransitNumber?: boolean;
	institutionBadgeDetails?: InstitutionBadgeDetails;
	fileType?: MatterFileType;
	mortgageeType?: MortgageeType;
	create: boolean;
}

type DischargePrimary = {
	email: string;
	email2: string;
	fax: string;
	phone: string;
	phone2: string;
};

type FormControlOf<T = Branch> = {
	[K in keyof T]: FormControl<T[K]>;
};

type Discharge = {
	primary: FormGroup<FormControlOf<DischargePrimary>>;
	escalation: FormGroup<FormControlOf<DischargePrimary>>;
};
interface BranchForm extends Omit<Branch, 'discharge'> {
	discharge: FormGroup<Discharge>;
}

@Component({
	selector: 'app-create-branch-institution',
	templateUrl: './create-branch.component.html',
	styleUrls: ['./create-branch.component.scss'],
	standalone: false,
})
export class CreateInstitutionBranchComponent implements OnInit {
	public loading = false;
	public submitted = false;
	public create = false;
	public branchLabel = 'Branch';
	public showTransitNumber = true;
	public mortgageeType: MortgageeType;
	public mortgageeTypes = MortgageeType;
	public showDischargeFields: boolean;

	public items = [
		{ value: false, label: 'Branch' },
		{ value: true, label: 'Institution' },
	];

	institutionLabels = institutionFields;
	labels = CompanyFields;
	addressLabels = AddressFields;

	public form = this.formBuilder.group<BranchForm>({
		id: createId(),
		transitNumber: null,
		address: null,
		phone: null,
		phone2: null,
		email: null,
		email2: null,
		fax: null,
		institutionAddressForService: false,
		institutionAddressForReport: false,
		name: null,
		discharge: this.formBuilder.group<Discharge>({
			primary: this.formBuilder.group<FormControlOf<DischargePrimary>>({
				email: null,
				email2: null,
				fax: null,
				phone: null,
				phone2: null,
			}),
			escalation: this.formBuilder.group<FormControlOf<DischargePrimary>>({
				email: null,
				email2: null,
				fax: null,
				phone: null,
				phone2: null,
			}),
		}),
	});

	public defaultInstitutionBranchId = '';
	public institutionId: string;
	public institutionBadgeDetails: InstitutionBadgeDetails;
	public contactTypes = ContactTypes;
	public contactType = ContactType;
	public selectedContactType: ContactType;

	constructor(
		public dialogRef: MatDialogRef<CreateInstitutionBranchComponent>,
		@Inject(MAT_DIALOG_DATA) public data: CreateOrUpdateInstitutionModel,
		private globalDataRequestDispatch: GlobalDataManagementRequestDispatch,
		private formBuilder: FormBuilder,
	) {}

	ngOnInit(): void {
		this.institutionId = this.data.institutionId;
		this.branchLabel = this.data.branchLabel ?? 'Branch';
		this.showDischargeFields = this.data.showDischargeFields;
		this.showTransitNumber = this.data.showTransitNumber ?? true;
		this.institutionBadgeDetails = this.data.institutionBadgeDetails;
		this.mortgageeType = this.data.mortgageeType;
		this.create = this.data.create;
		this.selectedContactType = getContactType(this.data?.fileType);
		if (this.data?.branch) {
			this.form.patchValue(this.data?.branch);
			if (this.create) {
				this.defaultInstitutionBranchId = this.data.branch?.id;
			}
		}
	}

	onSubmit(): void {
		this.submitted = true;
		if (this.form.valid) {
			const branch = this.form.getRawValue();
			this.globalDataRequestDispatch.RequestBranchChange(
				this.institutionId,
				branch,
			);
			this.loading = true;
			this.dialogRef.close(branch);
		}
	}
}
